import { useMemo, useState } from 'react';
import { SortConfig } from '../components/display/ProductSortControls';
import { PreparationLineWithProduct } from './usePreparationQuery';
import { useStableProducts } from './useStableProducts';
import type { RouteStop } from '@modules/WarehouseMap/models/types';

export const useProductSorting = (products: PreparationLineWithProduct[], routeStops?: RouteStop[] | null) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'route',
    direction: 'asc',
  });

  // Usar productos estables para evitar re-cálculos innecesarios
  const stableProducts = useStableProducts(products);

  // Build a map from location_id → route stop index for route-based sorting
  const routeLocationOrder = useMemo(() => {
    const map = new Map<number, number>();
    if (!routeStops) return map;
    routeStops.forEach((stop, index) => {
      map.set(stop.location_id, index);
    });
    return map;
  }, [routeStops]);

  const sortedProducts = useMemo(() => {
    if (!stableProducts || stableProducts.length === 0) {
      return { completed: [], pending: [] };
    }

    const completed = stableProducts.filter((line) => line.qty_done >= line.qty);
    const pending = stableProducts.filter((line) => line.qty_done < line.qty);

    const getRouteIndex = (line: PreparationLineWithProduct): number => {
      const locations = Array.isArray(line.suggested_locations) ? line.suggested_locations : [];
      for (const loc of locations) {
        const idx = routeLocationOrder.get(loc.id);
        if (idx !== undefined) return idx;
      }
      return Infinity;
    };

    const sortProducts = (items: PreparationLineWithProduct[]) => {
      return [...items].sort((a, b) => {
        let comparison = 0;

        switch (sortConfig.field) {
          case 'route':
            comparison = getRouteIndex(a) - getRouteIndex(b);
            break;

          case 'location': {
            const locationA = (Array.isArray(a.suggested_locations) && a.suggested_locations[0]?.code) || '';
            const locationB = (Array.isArray(b.suggested_locations) && b.suggested_locations[0]?.code) || '';
            comparison = locationA.localeCompare(locationB);
            break;
          }

          case 'name': {
            const nameA = a.product?.name || `Producto ID: ${a.product_id}`;
            const nameB = b.product?.name || `Producto ID: ${b.product_id}`;
            comparison = nameA.localeCompare(nameB);
            break;
          }

          case 'quantity':
            comparison = a.qty - a.qty_done - (b.qty - b.qty_done);
            break;

          default:
            comparison = 0;
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    };

    return {
      completed: sortProducts(completed),
      pending: sortProducts(pending),
    };
  }, [stableProducts, sortConfig, routeLocationOrder]);

  const handleSortChange = (newSortConfig: SortConfig) => {
    setSortConfig(newSortConfig);
  };

  return {
    sortConfig,
    sortedProducts,
    handleSortChange,
  };
};
